import { useRef } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Upload, ImageIcon } from 'lucide-react';
import { ImagePreview } from './ImagePreview';

interface FileUploadProps {
  label: string;
  description?: string;
  preview: string | null;
  onFileChange: (file: File | null) => void;
  accept?: string;
  disabled?: boolean;
}

export function FileUpload({
  label,
  description,
  preview,
  onFileChange,
  accept = 'image/*',
  disabled = false,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onFileChange(file);
  };

  const handleRemove = () => {
    onFileChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      
      {preview ? (
        <ImagePreview preview={preview} onRemove={handleRemove} label={label} />
      ) : (
        <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center hover:border-white/40 transition-colors">
          <Input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            disabled={disabled}
            className="hidden"
            id={`file-upload-${label}`}
          />
          <label
            htmlFor={`file-upload-${label}`}
            className="cursor-pointer flex flex-col items-center gap-2"
          >
            <div className="p-3 rounded-full bg-white/5">
              <ImageIcon className="h-6 w-6 text-white/60" />
            </div>
            <div className="text-sm text-white/60">
              Clique para fazer upload ou arraste o arquivo aqui
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Selecionar arquivo
            </Button>
          </label>
        </div>
      )}
    </div>
  );
}
